import React, {useState, useCallback} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import Form from 'react-bootstrap/Form';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {useStoreState, useStoreActions} from 'easy-peasy';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import usePaginatedData from '@/utils/usePaginatedData';
import ApiTokenSettingsModal from './ApiTokenSettingsModal';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import axios from 'axios';
import {useAlert} from '@/components/alerts';
import {ModalAlert} from '@/components/alerts';
import {extractErrorMessage} from '@/utils/errorHandling';

interface ApiToken {
    id: number;
    accessToken: string;
    user: {
        name: string;
    };
    updatedOn: string | null;
    createdOn: string;
    description: string | null;
    valid: boolean;
}

interface UserData {
    permissions?: string[];
}

interface AppConfig {
    DATETIME_FORMAT_REACT: string;
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
    error?: any;
}

const setSearchQueryState = async (
    setSearchQuery: (query: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const ApiTokenListView: React.FC = React.memo(() => {
    const appConfig = useStoreState<{ appConfigs: { items: AppConfig } }>((state) => state.appConfigs.items);
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedTokenId, setSelectedTokenId] = useState<number>(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [tokenToDelete, setTokenToDelete] = useState<{id: number, description: string | null} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const activeToken = useStoreState<{ apiTokens: { activeItem: string | null } }>((state) => state.apiTokens.activeItem);
    const setReloadApiTokens = useStoreActions((actions: any) => actions.apiTokens.setReload);
    const {showSuccess, showError} = useAlert();

    const [searchQuery, setSearchQuery] = useState<string>('');

    const {
        data: apiTokens,
        loading: loadingApiTokens,
        currentPage,
        setCurrentPage,
        pages,
        error: apiTokensError,
    }: PaginatedDataResult<ApiToken> = usePaginatedData(window.URLS.tokensResource, {
        storeResolver: 'apiTokens',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const canCreateApiToken = hasPermission('fs.api_token.create', userData?.permissions, true);
    const canChangeApiToken = hasPermission('fs.api_token.update', userData?.permissions, true);
    const canDeleteApiToken = hasPermission('fs.api_token.delete', userData?.permissions, true);

    const userCanAccessApiTokenResource = hasPermission('fs.api_token.list', userData?.permissions, true);

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (tokenId: number): void => {
        setSelectedTokenId(tokenId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedTokenId(0);
    };

    const openDeleteConfirm = useCallback((id: number, description: string | null): void => {
        setTokenToDelete({id, description});
        setDeleteConfirmOpen(true);
    }, []);

    const closeDeleteConfirm = useCallback((): void => {
        setDeleteConfirmOpen(false);
        setTokenToDelete(null);
        setIsDeleting(false);
    }, []);

    const confirmDelete = useCallback(async (): Promise<void> => {
        if (!tokenToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.tokensResource}/${tokenToDelete.id}`;
            await axios.delete(url);
            setReloadApiTokens(true);
            showSuccess(i18n.t('to_token_deleted.text') || 'Token deleted successfully');
            closeDeleteConfirm();
        } catch (error: any) {
            console.error('Failed to delete token:', error);
            const errorMessage = extractErrorMessage(error, i18n.t('to_token_delete_error.text') || 'Failed to delete token');
            showError(errorMessage);
            setIsDeleting(false);
        }
    }, [tokenToDelete, setReloadApiTokens, showSuccess, showError, closeDeleteConfirm]);

    const active = currentPage;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const isPermissionError = apiTokensError && (
        apiTokensError?.response?.status === 403 || 
        apiTokensError?.response?.data?.error === 'Permission denied'
    );

    if (loadingUserData) {
        return <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>;
    }

    if (!userCanAccessApiTokenResource && !loadingUserData) {
        return (
            <>
                <ConditionalNav />
                <HelpTextBlock helpText={i18n.t('ht_api_token.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    if (isPermissionError && !loadingApiTokens && !loadingUserData) {
        return (
            <>
                <ConditionalNav />
                <HelpTextBlock helpText={i18n.t('ht_api_token.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('th_token.text'),
            accessorKey: 'accessToken',
        },
        {
            header: i18n.t('th_username.text'),
            accessorKey: 'user.name',
        },
        {
            id: 'i18ntthlastmodified5text',
            header: i18n.t('th_last_modified.text'),
            cell: (cell: { row: { original: ApiToken } }) => {
                const row = cell.row.original;
                const dateTime = (!row.updatedOn || row.updatedOn === '-') ? row.createdOn : row.updatedOn;

                return (
                    <FormattedDateTime
                        dateTime={dateTime}
                        showTime={true}
                        options={{setZone: false}}
                        fallback={dateTime}
                    />
                );
            },
        },
        {
            header: i18n.t('th_description.text'),
            accessorKey: 'description',
            cell: (cell: { getValue: () => string | null }) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            header: i18n.t('th_valid.text'),
            accessorKey: 'valid',
            cell: (cell: { getValue: () => boolean }) => {
                if (cell.getValue() === true) {
                    return (
                        <Icon name="mdi-check" className="text-success" />
                    );
                } else {
                    return (
                        <Icon name="mdi-close" className="text-danger" />
                    );
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: { row: { original: ApiToken } }) => (
                <div className="d-inline-block">
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id={`settings-dropdown-${cell.row.original.id}`}
                                data-cy="token-settings-toggle"
                            />

                            <Dropdown.Menu align="end">
                                {canChangeApiToken ? (
                                    <Dropdown.Item
                                        onClick={() => openSettingsModal(cell.row.original.id)}
                                        data-cy="token-settings-open-button"
                                    >
                                        <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                    </Dropdown.Item>
                                ) : (
                                    <Tooltip
                                        content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div
                                            className="ml-auto"
                                            style={{cursor: 'not-allowed', display: 'flex'}}
                                        >
                                            <Dropdown.Item
                                                style={{pointerEvents: 'none'}}
                                                disabled
                                                data-cy="token-settings-open-button"
                                            >
                                                <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                            </Dropdown.Item>
                                        </div>
                                    </Tooltip>
                                )}
                                {canDeleteApiToken ? (
                                    <Dropdown.Item
                                        onClick={() => openDeleteConfirm(cell.row.original.id, cell.row.original.description)}
                                        data-cy="token-delete-button"
                                    >
                                        <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                    </Dropdown.Item>
                                ) : (
                                    <Tooltip
                                        content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div
                                            className="ml-auto"
                                            style={{cursor: 'not-allowed', display: 'flex'}}
                                        >
                                            <Dropdown.Item
                                                style={{pointerEvents: 'none'}}
                                                disabled
                                                data-cy="token-delete-button"
                                            >
                                                <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                            </Dropdown.Item>
                                        </div>
                                    </Tooltip>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <ConditionalNav>

            </ConditionalNav>

            <HelpTextBlock helpText={i18n.t('ht_api_token.text')} />

            {activeToken ? (
                <div className="alert alert-warning text-left">
                    {i18n.t('jt_make_sure.text')}
                    <br />
                    <b>{activeToken}</b>
                </div>
            ) : null}

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="token-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        canCreateApiToken ? (
                            <Button
                                variant="info"
                                size="sm"
                                className="mb-2"
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-token-btn"
                            >
                                {i18n.t('jt_add_new_api_token.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="mb-2"
                                        icon="mdi-plus"
                                        disabled
                                        data-cy="api-token-list-plus-btn">
                                        {i18n.t('jt_add_new_api_token.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingApiTokens}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent
                            data={apiTokens}
                            columns={columns}
                        />

                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
                {
                    settingsModalOpen ? (
                        <ApiTokenSettingsModal
                            modalOpen={settingsModalOpen}
                            closeModal={closeSettingsModal}
                            activeTokenId={selectedTokenId}
                        />
                    ) : null
                }
            </LoadingWrapper>
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={tokenToDelete ? i18n.t('jt_are_you_sure_delete_token.text', {description: tokenToDelete.description || tokenToDelete.id}) || `Are you sure you want to delete this token?` : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

export default ApiTokenListView; 